import numWords from "num-words";
import wordsToNumbers from "words-to-numbers";
import TooltipWrapper from "@/components/TooltipWrapper";
import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import WordCard from "@/components/LearningSession/TextWall/WordCard";
import "./TextWall.css";
import { speakText, stopSpeaking } from "@/utils/speechPlayer";
import { normalizeLanguageKey } from "@/constants/languageMap";

import useStore from "@/store";
import { THEME, accuracyToColor } from "@/lib/theme";

function debounce(func, delay) {
  let timeoutId;
  const cancel = () => {
    clearTimeout(timeoutId);
  };
  const debouncedFunc = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
  debouncedFunc.cancel = cancel;
  return debouncedFunc;
}

const speakWord = debounce((word, language, rate = 1) => {
  if (!word) {
    return;
  }

  const speed = typeof rate === "number" ? rate : 1;
  speakText(word, language, { speed });
}, 300);

const Space = () => (
  <span style={{ display: "inline-block", width: "0.5em" }}>&nbsp;</span>
);

// Strip punctuation from the start/end of a word, keeping apostrophes.
const stripSpecialChars = (str) =>
  str.replace(/^[.,"!;:?()[\]-]+|[.,"!;:?()[\]-]+$/g, "");

const Word = ({
  text,
  wrongWord,
  style,
  phonemes,
  transcribedPhonemes,
  phonemeMatches,
  accuracy,
  soundRisks,
  onWordClick,
}) => {
  const { settings } = useStore().settings;
  const { currentCollection } = useStore().collections;
  const { triggerPauseVideo } = useStore().learningSessionConfig;
  const collectionLanguage = currentCollection.language || "en";
  const normalizedCollectionLanguage = normalizeLanguageKey(collectionLanguage);
  const playbackLanguage =
    normalizedCollectionLanguage?.replace(/_/g, "-") || collectionLanguage;
  const nativeLanguageSetting = settings?.native_language || "en";
  const normalizedNativeLanguage = normalizeLanguageKey(nativeLanguageSetting);
  const nativeLanguageCode =
    normalizedNativeLanguage?.replace(/_/g, "-") || nativeLanguageSetting || "en";

  useEffect(() => {
    return () => {
      speakWord.cancel();
    };
  }, []);

  const displayText = text.replace(/\n/g, "");
  const cleanWord = stripSpecialChars(displayText);

  return (
    <React.Fragment>
      {text.includes("\n") && (
        <>
          <br />
          <br />
        </>
      )}
      <span style={style} className="text-lg md:text-xl">
        <Popover
          onOpenChange={(isOpen) => {
            if (isOpen) {
              triggerPauseVideo();
            }
            onWordClick();
          }}
        >
          <PopoverTrigger className="cursor-pointer rounded-sm transition hover:text-primary focus-visible:text-primary">
            {displayText}
          </PopoverTrigger>
          <PopoverContent
            className="w-[min(340px,calc(100vw-32px))] p-4"
            collisionPadding={16}
          >
            <WordCard
              word={cleanWord}
              language={playbackLanguage}
              nativeLanguage={nativeLanguageCode}
              phonemes={phonemes}
              phonemeMatches={phonemeMatches}
              transcribedPhonemes={transcribedPhonemes}
              accuracy={accuracy}
              soundRisks={soundRisks}
            />
            {wrongWord && wrongWord !== "0" && (
              <p className="mt-3 text-sm text-fg-muted">
                Heard: <span className="text-danger">{stripSpecialChars(wrongWord)}</span>
              </p>
            )}
          </PopoverContent>
        </Popover>
      </span>
    </React.Fragment>
  );
};

const Spacer = () => (
  <span style={{ display: "inline-block", width: "8px" }}></span>
); // Adjust width as needed

const Sentence = ({ sentence, isActive, words }) => {
  const {
    user,
    onboardingEnabled,
    onboardingStages,
    setOnboardingStage,
    setOnboardingEnabled,
  } = useStore().user;
  const [showWordClickOnboarding, setShowWordClickOnboarding] = useState(false);
  const sentenceStyle = isActive ? {} : {};
  const { currentCollection } = useStore().collections;
  const sentenceCollectionLanguage = currentCollection.language || "en";
  const normalizedSentenceLanguage = normalizeLanguageKey(
    sentenceCollectionLanguage
  );
  const sentenceLanguage =
    normalizedSentenceLanguage?.replace(/_/g, "-") ||
    sentenceCollectionLanguage;

  useEffect(() => {
    if (
      onboardingEnabled &&
      onboardingStages.speakButtonClicked &&
      !onboardingStages.wordClicked &&
      words[0].phonemes
    ) {
      setShowWordClickOnboarding(true);
    } else {
      setShowWordClickOnboarding(false);
    }
  }, [onboardingEnabled, onboardingStages, words]);

  const handleWordClick = () => {
    if (showWordClickOnboarding) {
      setOnboardingStage("wordClicked", true);
      setOnboardingEnabled(false);
    }
  };

  return (
    <TooltipWrapper
      tooltipText={"Speak"}
      onboarding={showWordClickOnboarding}
      onboardingMessage="Click on any word to listen, translate or practice separately."
    >
      <span key={sentence} style={sentenceStyle}>
        {sentenceLanguage.split("-")[0] === "ar" ? (
          words.map((word, index) => (
            <React.Fragment key={index}>
              {index >= 0 && <Spacer />}
              <Word
                text={word.text}
                style={word.style}
                wrongWord={word.wrongWord}
                phonemes={word.phonemes}
                transcribedPhonemes={word.transcribed_phonemes}
                phonemeMatches={word.phoneme_matches}
                accuracy={word.accuracy}
                soundRisks={word.sound_risks}
                onWordClick={handleWordClick}
              />
            </React.Fragment>
          )).reverse()
        ) : (
          words.map((word, index) => (
            <React.Fragment key={index}>
              <Word
                text={word.text}
                style={word.style}
                wrongWord={word.wrongWord}
                phonemes={word.phonemes}
                transcribedPhonemes={word.transcribed_phonemes}
                phonemeMatches={word.phoneme_matches}
                accuracy={word.accuracy}
                soundRisks={word.sound_risks}
                onWordClick={handleWordClick}
              />
              {index < words.length - 1 && <Spacer />}
            </React.Fragment>
          ))
        )}
      </span>
    </TooltipWrapper>
  );
};

function processPhonemes(displayedText, phonemes) {
  displayedText = displayedText.replace(/\n/g, " ");
  const displayedSentences = displayedText.split(/\n+/);
  let elements = [];

  if (phonemes.length === 0) {
    displayedSentences.forEach((sentence) => {
      const words = sentence
        .split(" ")
        .filter((word) => word.trim() !== "")
        .map((word, index) => {
          return { text: word, style: {} };
        });

      elements.push(
        <Sentence
          key={sentence}
          sentence={sentence}
          isActive={true}
          words={words}
        />
      );
    });
  } else {
    const updatedPhonemes = phonemes.map((phoneme) => ({
      ...phoneme,
      style: {
        color: accuracyToColor(phoneme.accuracy),
      },
    }));

    elements.push(
      <Sentence
        key={updatedPhonemes[0].word}
        sentence={updatedPhonemes[0].word}
        isActive={true}
        words={updatedPhonemes}
      />
    );
  }

  return elements;
}

function processTexts(
  resultText,
  interimResultText,
  displayedText,
  activeSentence
) {
  const resultWords = (resultText + " " + interimResultText)
    .split(" ")
    .filter((word) => word.trim() !== "");

  resultWords.forEach((word, index) => {
    // Convert numbers to words for comparison
    if (!isNaN(word)) {
      // If the word is a numeric value
      resultWords[index] = numWords(parseInt(word, 10));
    } else {
      // Attempt to convert word to number and back to word to normalize words like "forty-two" and "42"
      const possibleNumber = wordsToNumbers(word);
      if (!isNaN(possibleNumber)) {
        resultWords[index] = numWords(possibleNumber);
      }
    }
  });

  displayedText = displayedText.replace(/\n/g, " ");

  const displayedSentences = displayedText.split(/\n+/);

  let elements = [];
  let currentWordIndex = 0;

  displayedSentences.forEach((sentence) => {
    const isActive = sentence.trim() === activeSentence.trim();
    const sentenceWords = sentence
      .split(" ")
      .filter((word) => word.trim() !== "");

    const words = sentenceWords.map((word, index) => {
      const cleanWord = word.toLowerCase().replace(/[^\p{L}\p{N}\s]|_/gu, "");
      const expectedWord = resultWords[currentWordIndex]
        ? resultWords[currentWordIndex]
            .toLowerCase()
            .replace(/[^\p{L}\p{N}\s]|_/gu, "")
        : null;

      let wrongWord = null;
      // Logic to determine if the word is wrong and set wrongWord accordingly
      if (expectedWord && cleanWord !== expectedWord) {
        wrongWord = expectedWord; // This assumes you have a way to identify the wrong word
      }

      let style = {};
      if (currentWordIndex < resultWords.length) {
        style = {
          color: cleanWord === expectedWord ? THEME.primary : THEME.danger,
        };
      }

      currentWordIndex++;
      return { text: word, wrongWord, style };
    });

    elements.push(
      <Sentence
        key={sentence}
        sentence={sentence}
        isActive={isActive}
        words={words}
      />
    );
  });

  return elements;
}

const TextWall = ({
  text,
  transcribedSpeechText,
  interimTranscribedSpeechText,
  activeSentence,
  endOfTextReached,
  phonemes,
  mode = "realtime",
}) => {
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });

  const textWallRef = useRef(null);
  const tooltipRef = useRef(null);

  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [hasEndOfTextBeenReached, setHasEndOfTextBeenReached] = useState(false);

  useEffect(() => {
    if (hasEndOfTextBeenReached || transcribedSpeechText.length === 0) {
      setHasEndOfTextBeenReached(false);
    }
  }, [text, transcribedSpeechText]);

  useEffect(() => {
    return () => {
      stopSpeaking();
    };
  }, []);

  const checkScrollability = () => {
    if (textWallRef.current) {
      const element = textWallRef.current;
      setCanScrollUp(element.scrollTop > 0);
      setCanScrollDown(
        element.scrollTop < element.scrollHeight - element.clientHeight
      );
    }
  };

  useEffect(() => {
    const element = textWallRef.current;
    if (element) {
      checkScrollability();
      element.addEventListener("scroll", checkScrollability);

      return () => element.removeEventListener("scroll", checkScrollability);
    }
  }, []);

  useEffect(() => {
    if (!hasEndOfTextBeenReached) {
      // Normalize and prepare the texts for comparison
      const normalizeText = (text) =>
        text
          .toLowerCase()
          .replace(/[^\p{L}\p{N}\s]|_/gu, "")
          .split(/\s+/)
          .filter(Boolean);
      let transcribedWords = normalizeText(
        transcribedSpeechText + " " + interimTranscribedSpeechText
      );

      transcribedWords.forEach((word, index) => {
        // Convert numbers to words for comparison
        if (!isNaN(word)) {
          // If the word is a numeric value
          transcribedWords[index] = numWords(parseInt(word, 10));
        } else {
          // Attempt to convert word to number and back to word to normalize words like "forty-two" and "42"
          const possibleNumber = wordsToNumbers(word);
          if (!isNaN(possibleNumber)) {
            transcribedWords[index] = numWords(possibleNumber);
          }
        }
      });

      const originalWords = normalizeText(text);
      let wrongWords = []; // Initialize an array to hold the wrong words
      let correctCount = 0;

      originalWords.forEach((word, index) => {
        if (transcribedWords[index] === word) {
          correctCount++;
        } else if (transcribedWords[index] !== undefined) {
          // Ensure comparison only when there's a transcribed word
          wrongWords.push(word); // Add the wrong word to the array
        }
      });

      const accuracy =
        originalWords.length > 0
          ? (correctCount / originalWords.length) * 100
          : 0;
      if (
        transcribedWords.length >= originalWords.length &&
        interimTranscribedSpeechText.trim() === ""
      ) {
        endOfTextReached(accuracy, originalWords, transcribedWords, wrongWords);
        setHasEndOfTextBeenReached(true);
      }
    }
  }, [
    transcribedSpeechText,
    interimTranscribedSpeechText,
    text,
    endOfTextReached,
    hasEndOfTextBeenReached,
  ]);

  // Custom styles to encourage scrollbar visibility
  const textWallStyles = {
    overflowY: "scroll", // Encourage vertical scrollbar
    maxHeight: "100%", // Adjust as needed to fit your layout
    paddingRight: "2px", // Prevent text clipping by scrollbar
    // Additional styles for your text wall container
  };

  // Scrollbar styles for WebKit browsers
  const scrollBarStyles = `
  ::-webkit-scrollbar {
    width: 12px; // Adjust the width of the scrollbar as needed
  }
  ::-webkit-scrollbar-thumb {
    background-color: rgba(0,0,0,0.2); // Adjust the color of the scroll thumb as needed
    border-radius: 6px; // Rounded corners for the scroll thumb
  }
  ::-webkit-scrollbar-track {
    background-color: transparent; // Ensure the track background doesn't introduce unwanted visual elements
  }
  
  ::-webkit-scrollbar-corner {
    background-color: transparent; /* or your desired corner color */
  }
  
`;

  return (
    <div
      ref={textWallRef}
      style={textWallStyles}
      // style={{
      //   boxShadow: `${
      //     canScrollUp ? "inset 0 15px 10px -10px rgba(0, 0, 0, 0.15)" : ""
      //   }${canScrollUp && canScrollDown ? "," : ""}${
      //     canScrollDown ? "inset 0 -15px 10px -10px rgba(0, 0, 0, 0.15)" : ""
      //   }`,
      // }}
    >
      <style>{scrollBarStyles}</style>

      <p className="first-step leading-10 whitespace-pre-wrap">
        {mode === "realtime"
          ? processTexts(
              transcribedSpeechText,
              interimTranscribedSpeechText,
              text,
              activeSentence
            )
          : mode === "phoneme"
            ? processPhonemes(text, phonemes)
            : null}
      </p>
    </div>
  );
};

export default TextWall;
